import type { ReactNode } from "react";
import { Settings2 } from "lucide-react";
import { Tooltip, HelpLabel } from "@/components/Wizard";
import { InfoPopover, type LearnMore } from "@/components/InfoPopover";

export { Tooltip, HelpLabel, InfoPopover };
export type { LearnMore };

const PAGE_HELP: Record<string, { what: string; why: string; who: string }> = {
  Pipelines: {
    what: "Build and run a reviewed sequence of processing steps.",
    why: "Saved revisions make each run reproducible and explain its result.",
    who: "Workspace editors can draft; protected run and publish actions follow their own role checks.",
  },
  "Metadata Profiles": {
    what: "Define reusable context and checks for incoming content.",
    why: "Published versions can be attached to Catalog sources and used as retrieval filters.",
    who: "Workspace editors can draft; admins or owners publish versions.",
  },
  "Domain Packs": {
    what: "Inspect and install eligible, versioned domain assets.",
    why: "Pinned packs keep ontology inputs and usage rights traceable.",
    who: "Workspace admins or owners manage installations and publishing.",
  },
  "Term-set library": {
    what: "Review reusable terms and their immutable versions.",
    why: "Pinned versions supply approved vocabulary to ontology drafts and retrieval.",
    who: "Editors prepare changes; admins or owners approve reviewed terms.",
  },
  "Developer portal": {
    what: "Configure scoped access to bundles and runtime APIs.",
    why: "Consumers can resolve pinned assets without broad workspace credentials.",
    who: "Workspace admins or owners manage service accounts and bindings.",
  },
  Workspaces: {
    what: "Choose and manage workspaces within your tenant.",
    why: "Each workspace keeps its own membership and knowledge assets.",
    who: "Tenant owners or admins create workspaces and may explicitly join another workspace.",
  },
  Users: {
    what: "Review members, invitations, roles and plan usage.",
    why: "Membership controls which workspace data each person can reach.",
    who: "Workspace owners or admins manage memberships and invitations.",
  },
  "Workspace settings": {
    what: "Set this workspace's display and runtime preferences.",
    why: "Defaults guide new work without changing published versions.",
    who: "Workspace owners or admins manage protected settings.",
  },
};

export function PageHeader({
  title, subtitle, help, learnMore, actions, who,
}: {
  title: string;
  subtitle?: string;
  help?: ReactNode;
  learnMore?: LearnMore | LearnMore[];
  actions?: ReactNode;
  who?: string;
}) {
  const guide = PAGE_HELP[title];
  return (
    <div data-page-header className="mb-5 flex min-w-0 flex-wrap items-start justify-between gap-x-5 gap-y-3 border-t border-border pt-4 sm:mb-6">
      <div className="min-w-0 max-w-3xl">
        <h1 className="flex items-center gap-2 text-2xl font-medium tracking-tight">
          {title}
          <InfoPopover title={title} label={`Help for ${title}`} learnMore={learnMore}>
            {help ?? <>
              <p><strong>What:</strong> {guide?.what ?? subtitle ?? `Explore ${title.toLowerCase()} in this workspace.`}</p>
              <p><strong>Why:</strong> {guide?.why ?? "Use this page as part of the reviewed workspace workflow."}</p>
            </>}
            <p><strong>Who can edit:</strong> {who ?? guide?.who ?? "Workspace roles govern changes; protected actions may require an admin or owner."}</p>
          </InfoPopover>
        </h1>
        {subtitle && <p className="mt-1 max-w-[72ch] text-sm leading-relaxed text-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="card text-center text-muted">
      <div className="text-sm">{title}</div>
      {hint && <div className="mt-1 text-xs">{hint}</div>}
    </div>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  const msg = error instanceof Error ? error.message : String(error);
  const status = (error as Error & { status?: number })?.status;

  if (status === 403) {
    return <div className="card border-bad/40 text-sm text-bad" role="alert">
      <p className="font-medium">You don't have permission for this action.</p>
      <p className="mt-1">{msg}</p>
    </div>;
  }

  // 503 with "migration pending" context — show a distinct banner
  if (status === 503 || msg.toLowerCase().includes("migration")) {
    return (
      <div className="card border-warn/40 bg-warn/5 text-sm">
        <div className="flex items-start gap-2">
          <Settings2 size={16} className="mt-0.5 shrink-0 text-warn" />
          <div>
            <p className="font-medium text-warn">Setup in progress</p>
            <p className="mt-0.5 text-muted">{msg}</p>
          </div>
        </div>
      </div>
    );
  }

  return <div className="card border-bad/40 text-sm text-bad">Error: {msg}</div>;
}

export function StatusPill({ status }: { status?: string | null }) {
  const s = (status ?? "").toLowerCase();
  const cls =
    s === "succeeded" || s === "ok" || s === "active"   ? "pill-good" :
    s === "failed"    || s === "error"                  ? "pill-bad"  :
    s === "running"   || s === "pending" || s === "draft" ? "pill-warn"
                                                          : "pill";
  return <span className={cls}>{status ?? "—"}</span>;
}

interface Col<T> { header: ReactNode; cell: (row: T) => ReactNode; width?: string }
export function DataTable<T extends { id?: string }>({ rows, cols, empty, rowClassName }: {
  rows: T[]; cols: Col<T>[]; empty?: string; rowClassName?: (row: T) => string | undefined;
}) {
  if (!rows || rows.length === 0)
    return <EmptyState title={empty ?? "No rows yet."} />;
  return (
    <div className="card overflow-x-auto overflow-y-visible p-0">
      <table className="min-w-full text-sm">
        <thead className="bg-border/30 text-left text-xs uppercase tracking-wide text-muted">
          <tr>
            {cols.map((c, i) => (
              <th key={i} className="px-3 py-2 font-medium" style={{ width: c.width }}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id ?? i} className={["border-t border-border/60", rowClassName?.(r) ?? ""].join(" ").trim()}>
              {cols.map((c, j) => (
                <td key={j} className="px-3 py-2 align-top">{c.cell(r)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
